import React, {
  FC, useEffect, useMemo, useState
} from 'react';
import {
  AnimatePresence
} from 'framer-motion';
import {
  Trans, useTranslation
} from 'react-i18next';
import {
  useNavigate, useParams
} from 'react-router-dom';

import {
  DeleteOrganizationModal,
  Filters,
  OrganisationTable,
} from 'src/features';
import {
  PageLayout, OrganisationSettings
} from 'src/widgets';
import {
  SettingOptions
} from 'src/widgets/OrganisationSettings/OrganisationSettings';
import {
  Button,
  ModalLeave,
  OrganizationSettingsModal,
  Spinner,
} from 'src/shared/components';
import {
  ROUTE
} from 'src/shared/utils';
import {
  useGetApiOrganizationsQuery,
  useGetOrganizationQuery,
  UserGroupRole,
} from 'src/redux/openapi';
import {
  QuestionSetControls
} from 'src/widgets/QuestionSetControls';

import * as Style from './Organisation.styles';

export const Organisation: FC = () => {
  const {
    t
  } = useTranslation();

  const [orgIdToModify, setOrgIdToModify] = useState('');

  const [actionWithOrg, setActionWithOrg] = useState<SettingOptions>(
    '' as SettingOptions,
  );

  const params = useParams();

  const {
    data: organizationsData,
    isLoading,
    refetch,
  } = useGetApiOrganizationsQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const organizations = useMemo(
    () => organizationsData?.data.organizations || [],
    [organizationsData],
  );

  const activeFilterId = params.organizationId
    || organizationsData?.data?.organizations[0]?.id
    || null;

  const currentOrganization = organizations.find(
    (organization) => organization.id === activeFilterId,
  );

  const organizationsForFilter = organizations.map(({
    id, name, role
  }) => ({
    id,
    label: name,
    isAdmin: role === UserGroupRole.SUPERADMIN,
  }));

  const navigate = useNavigate();

  const onFilterClick = (id: string | null) => {
    if (!id) {
      navigate(`/${ROUTE.ORGANISATION}`);
    } else if (id !== activeFilterId) {
      navigate(`/${ROUTE.ORGANISATION}/${id}`);
    }
  };

  const orgToModify = organizations.find(({
    id
  }) => id === orgIdToModify);

  const {
    data: orgToModifyDetailData
  } = useGetOrganizationQuery(
    {
      id: orgIdToModify,
    },
    {
      skip: !orgIdToModify || actionWithOrg !== SettingOptions.Settings,
      refetchOnMountOrArgChange: true,
    },
  );

  const orgToModifyWithDetails = orgToModifyDetailData?.data.organization
    ? {
      ...orgToModifyDetailData.data.organization,
      role: orgToModify?.role ?? 'USER',
      memberCount: orgToModify?.memberCount ?? 0,
      groupCount: orgToModify?.groupCount ?? 0,
      invitedCount: orgToModify?.invitedCount ?? 0,
      suggestionsEnabled: orgToModify?.suggestionsEnabled ?? false,
    }
    : orgToModify;

  const handleOpenModalConfirm = (id: string, action: SettingOptions) => {
    setOrgIdToModify(id);
    setActionWithOrg(action);
  };

  const handleCloseModal = () => {
    setOrgIdToModify('');
    setActionWithOrg('' as SettingOptions);
  };

  const handleSetUpOrganizationClick = (idPath: string) => {
    navigate(`/${ROUTE.ORGANISATION}/${idPath}/${ROUTE.SETUP_ORGANISATION}`);
  };

  // TODO: delete navigation if all org page will be enabled
  useEffect(
    () => {
      if (organizations.length && !params.organizationId) {
        navigate(
          `/${ROUTE.ORGANISATION}/${organizations[0].id}`,
          {
            replace: true,
          }
        );
      }
    },
    [params.organizationId, organizations]
  );

  return (
    <PageLayout>
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {!organizations.length && (
            <Style.EmptyMessageContainer>
              <Style.EmptyMessageTitle>
                {t('organisation.notMemberOfOrganisation')}
              </Style.EmptyMessageTitle>

              <Style.EmptyMessageSubtitle>
                <Trans i18nKey="organisation.submitRequestToPromote" />
              </Style.EmptyMessageSubtitle>

              <Style.EmptyMessageLink to={`/${ROUTE.GROUPS}`}>
                {t('organisation.goBackToGroups')}
              </Style.EmptyMessageLink>
            </Style.EmptyMessageContainer>
          )}

          {!!organizations.length && (
            <>
              {currentOrganization && currentOrganization.isSetUp && (
                <Style.HeaderContainer>
                  <Filters
                    items={organizationsForFilter}
                    onFilterClick={onFilterClick}
                    activeFilterId={activeFilterId}
                    renderMenuContent={(el) => {
                      if (!el.id) {
                        return null;
                      }

                      return (
                        <OrganisationSettings
                          isAdmin={el.isAdmin}
                          openModalConfirm={(action: SettingOptions) => {
                            handleOpenModalConfirm(
                              el.id,
                              action
                            );
                          }}
                        />
                      );
                    }}
                  />

                  <QuestionSetControls />
                </Style.HeaderContainer>
              )}

              {currentOrganization && !currentOrganization.isSetUp && (
                <Style.EmptyMessageContainer>
                  <Style.CreateMessageContainer>
                    <Style.EmptyMessageTitle>
                      {t('organisation.createYourOrganisation')}
                    </Style.EmptyMessageTitle>

                    <Style.EmptyMessageSubtitle>
                      <Trans i18nKey="organisation.organizationApproved" />
                    </Style.EmptyMessageSubtitle>

                    <Button
                      variant="big-blue"
                      className="font-bold text-xl w-max m-auto"
                      onClick={() => handleSetUpOrganizationClick(currentOrganization.id)}
                    >
                      {t('organisation.setUpOrganisation')}
                    </Button>
                  </Style.CreateMessageContainer>
                </Style.EmptyMessageContainer>
              )}

              {currentOrganization && currentOrganization.isSetUp && (
                <Style.OrganisationWrapper>
                  <OrganisationTable organization={currentOrganization} />
                </Style.OrganisationWrapper>
              )}

              <ModalLeave
                isOpen={actionWithOrg === SettingOptions.Leave}
                closeModal={handleCloseModal}
                organization={orgToModify}
                onLeave={refetch}
              />

              <DeleteOrganizationModal
                isOpen={actionWithOrg === SettingOptions.Delete}
                closeModal={handleCloseModal}
                id={orgToModify?.id}
                onSuccessDelete={refetch}
              />

              {actionWithOrg === SettingOptions.Settings
                && orgToModifyWithDetails && (
                  <div
                    className="fixed inset-0 z-[19]"
                    role="button"
                    tabIndex={-1}
                    aria-label={t('buttons.close')}
                    onClick={handleCloseModal}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        handleCloseModal();
                      }
                    }}
                  />
              )}

              <AnimatePresence>
                {actionWithOrg === SettingOptions.Settings
                  && orgToModifyWithDetails && (
                    <OrganizationSettingsModal
                      handleCloseModal={handleCloseModal}
                      organization={orgToModifyWithDetails}
                    />
                )}
              </AnimatePresence>
            </>
          )}
        </>
      )}
    </PageLayout>
  );
};
